import { useState } from 'react';
import { Shield, Lock, Download, TrendingUp, Users, Building2, ChevronRight, Check, X } from 'lucide-react';

export default function OrionFundTab({ user }) {
  const [showEnquiryForm, setShowEnquiryForm] = useState(false);
  const [formData, setFormData] = useState({
    name: user?.name || '',
    email: user?.email || '',
    phone: '',
    netWorth: '',
    investmentIntent: '',
    message: ''
  });
  const [enquirySubmitted, setEnquirySubmitted] = useState(false);
  const [eligibilityCheck, setEligibilityCheck] = useState(null);

  const handleSubmitEnquiry = (e) => {
    e.preventDefault();
    setEnquirySubmitted(true);
    setTimeout(() => {
      setShowEnquiryForm(false);
      setEnquirySubmitted(false);
    }, 3000);
  };

  const checkEligibility = (netWorthRange) => {
    if (netWorthRange === '5cr+' || netWorthRange === '10cr+') {
      setEligibilityCheck('eligible');
    } else if (netWorthRange === '2-5cr') {
      setEligibilityCheck('borderline');
    } else {
      setEligibilityCheck('not-eligible');
    }
  };

  return (
    <div className="p-10 max-w-5xl mx-auto">
      {/* Header with Logo */}
      <div className="text-center mb-12">
        <div className="flex justify-center mb-6">
          <div className="bg-white p-6 border border-gray-200">
            <img src="/orion-logo.png" alt="Orion" className="h-16" onError={(e) => {
              e.target.outerHTML = '<p class="text-4xl font-bold text-black">ORION</p>';
            }} />
          </div>
        </div>
        <h1 className="text-3xl font-light text-white mb-2">AltSlice · Orion Realty Fund</h1>
        <p className="text-sm text-gray-500">Category II Alternative Investment Fund</p>
      </div>

      {/* Invitation Only Banner */}
      <div className="bg-yellow-500/10 border border-yellow-500/30 p-8 mb-12">
        <div className="flex items-start gap-4">
          <Lock className="w-6 h-6 text-yellow-500 mt-1" />
          <div>
            <p className="text-white font-medium mb-2">INVITATION ONLY - SOPHISTICATED INVESTORS</p>
            <p className="text-sm text-gray-400 mb-3">This fund is available by invitation only for sophisticated investors as per SEBI guidelines.</p>
            <p className="font-mono text-2xl text-yellow-500">Minimum Contribution: ₹1,00,00,000</p>
          </div>
        </div>
      </div>

      {/* Fund Strategy */}
      <div className="mb-12">
        <h2 className="text-xl text-white mb-6">Fund Strategy</h2>
        <p className="text-gray-400 mb-8">The Orion Realty Fund focuses on three high-value institutional opportunities:</p>
        
        <div className="space-y-6">
          <div className="border-l-2 border-white/20 pl-6">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-8 h-8 bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                <span className="text-sm text-white font-medium">1</span>
              </div>
              <div>
                <h3 className="text-lg text-white font-medium mb-2">Distress Developer Inventory Aggregation</h3>
                <p className="text-sm text-gray-500">Acquire under-construction assets from financially stressed developers at significant discounts. Restructure and exit at market value for accelerated returns.</p>
              </div>
            </div>
          </div>

          <div className="border-l-2 border-white/20 pl-6">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-8 h-8 bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                <span className="text-sm text-white font-medium">2</span>
              </div>
              <div>
                <h3 className="text-lg text-white font-medium mb-2">Land Aggregation for Institutional JVs</h3>
                <p className="text-sm text-gray-500">Consolidate fragmented land parcels in high-growth corridors. Partner with institutional developers for joint venture developments.</p>
              </div>
            </div>
          </div>

          <div className="border-l-2 border-white/20 pl-6">
            <div className="flex items-start gap-3 mb-3">
              <div className="w-8 h-8 bg-white/5 border border-white/10 flex items-center justify-center flex-shrink-0">
                <span className="text-sm text-white font-medium">3</span>
              </div>
              <div>
                <h3 className="text-lg text-white font-medium mb-2">Higher-Than-Standard Value Unlock</h3>
                <p className="text-sm text-gray-500">Opportunistic investments with IRR targets of 20-30% p.a. through strategic exits, value engineering, and institutional partnerships.</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Fund Details Grid */}
      <div className="grid grid-cols-3 gap-px bg-white/5 border border-white/10 mb-12">
        <div className="bg-black px-6 py-6">
          <p className="text-xs text-gray-600 uppercase tracking-widest mb-2">Fund Size</p>
          <p className="font-mono text-2xl text-white">₹500 Cr</p>
        </div>
        <div className="bg-black px-6 py-6">
          <p className="text-xs text-gray-600 uppercase tracking-widest mb-2">Target IRR</p>
          <p className="font-mono text-2xl text-[#4ADE80]">25-30%</p>
          <p className="text-xs text-gray-600">p.a.</p>
        </div>
        <div className="bg-black px-6 py-6">
          <p className="text-xs text-gray-600 uppercase tracking-widest mb-2">Investment Term</p>
          <p className="font-mono text-2xl text-white">3-5 Yrs</p>
        </div>
      </div>

      {/* Additional Details */}
      <div className="grid grid-cols-3 gap-6 mb-12">
        <div className="border border-white/10 p-6">
          <p className="text-xs text-gray-600 uppercase tracking-wider mb-3">Min Investment</p>
          <p className="font-mono text-xl text-white">₹1.00 Cr</p>
        </div>
        <div className="border border-white/10 p-6">
          <p className="text-xs text-gray-600 uppercase tracking-wider mb-3">Lock-in Period</p>
          <p className="text-white">3 years</p>
        </div>
        <div className="border border-white/10 p-6">
          <p className="text-xs text-gray-600 uppercase tracking-wider mb-3">Structure</p>
          <p className="text-white">Category II AIF</p>
        </div>
      </div>

      {/* Mock Historical Performance */}
      <div className="mb-12">
        <h2 className="text-xl text-white mb-6">Historical Performance</h2>
        <div className="border border-white/10 p-8">
          <div className="grid grid-cols-4 gap-8">
            {[
              { year: 'FY 2022-23', return: '27.3%' },
              { year: 'FY 2023-24', return: '31.2%' },
              { year: 'FY 2024-25', return: '28.7%' },
              { year: '3-Yr Avg', return: '29.1%' }
            ].map((data, idx) => (
              <div key={idx}>
                <p className="text-xs text-gray-600 mb-2">{data.year}</p>
                <p className="font-mono text-3xl text-[#4ADE80]">{data.return}</p>
              </div>
            ))}
          </div>
          <p className="text-xs text-gray-600 mt-6">*Past performance is not indicative of future results. Subject to market risks.</p>
        </div>
      </div>

      {/* Eligibility Checker */}
      <div className="mb-12">
        <h2 className="text-xl text-white mb-6">Check Your Eligibility</h2>
        <div className="border border-white/10 p-8">
          <p className="text-sm text-gray-400 mb-6">Select your net worth range (excluding primary residence):</p>
          <div className="grid grid-cols-4 gap-4 mb-6">
            {[
              { value: 'below-2cr', label: 'Below ₹2 Cr' },
              { value: '2-5cr', label: '₹2-5 Cr' },
              { value: '5cr+', label: '₹5+ Cr' },
              { value: '10cr+', label: '₹10+ Cr' }
            ].map((option) => (
              <button
                key={option.value}
                onClick={() => checkEligibility(option.value)}
                className="border border-white/10 hover:border-white/20 p-4 text-sm text-white transition-all"
              >
                {option.label}
              </button>
            ))}
          </div>

          {eligibilityCheck === 'eligible' && (
            <div className="bg-green-500/10 border border-green-500/30 p-6 flex items-start gap-4">
              <Check className="w-6 h-6 text-green-500" />
              <div>
                <p className="text-white font-medium mb-1">You appear to be eligible</p>
                <p className="text-sm text-gray-400">Submit an enquiry below to proceed with the application process.</p>
              </div>
            </div>
          )}

          {eligibilityCheck === 'borderline' && (
            <div className="bg-yellow-500/10 border border-yellow-500/30 p-6 flex items-start gap-4">
              <Shield className="w-6 h-6 text-yellow-500" />
              <div>
                <p className="text-white font-medium mb-1">Borderline Eligibility</p>
                <p className="text-sm text-gray-400">You may qualify. Please submit an enquiry for our team to review your profile.</p>
              </div>
            </div>
          )}

          {eligibilityCheck === 'not-eligible' && (
            <div className="bg-red-500/10 border border-red-500/30 p-6 flex items-start gap-4">
              <X className="w-6 h-6 text-red-500" />
              <div>
                <p className="text-white font-medium mb-1">Eligibility Criteria Not Met</p>
                <p className="text-sm text-gray-400">This fund requires a minimum net worth of ₹5 Crore. Explore our fractional products (Land & Residences) with lower entry points.</p>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Download Factsheet */}
      <div className="mb-12">
        <button className="border border-white/10 hover:border-white/20 px-8 py-4 text-white text-sm uppercase tracking-wider flex items-center gap-3 transition-all">
          <Download className="w-5 h-5" />
          Download Fund Factsheet (PDF)
        </button>
      </div>

      {/* Eligibility Section */}
      <div className="mb-12">
        <h2 className="text-xl text-white mb-6">Investor Eligibility</h2>
        <div className="border border-white/10 p-8">
          <p className="text-sm text-gray-400 mb-6">This fund is suitable for:</p>
          <div className="space-y-3">
            {[
              'Sophisticated investors as per SEBI AIF regulations',
              'Individuals with net worth > ₹5 Crore (excluding primary residence)',
              'Institutions and family offices',
              'Investors seeking alternative real estate exposure',
              'Long-term capital allocation (3-5 year horizon)'
            ].map((criteria, idx) => (
              <div key={idx} className="flex items-start gap-3">
                <Check className="w-4 h-4 text-[#4ADE80] mt-1" />
                <p className="text-sm text-gray-400">{criteria}</p>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Enquiry Form */}
      <div className="mb-12">
        <div className="border border-white/10 p-8">
          <h2 className="text-xl text-white mb-4">Investment Enquiry</h2>
          <p className="text-sm text-gray-500 mb-8">This is an invitation-only fund. Submit your details and our fund team will review your profile and reach out within 48 hours.</p>

          {!enquirySubmitted ? (
            <form onSubmit={handleSubmitEnquiry} className="space-y-6">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <label className="text-xs text-gray-600 uppercase tracking-wider mb-2 block">Full Name</label>
                  <input value={formData.name} onChange={(e) => setFormData({...formData, name: e.target.value})} className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white" required />
                </div>
                <div>
                  <label className="text-xs text-gray-600 uppercase tracking-wider mb-2 block">Email</label>
                  <input type="email" value={formData.email} onChange={(e) => setFormData({...formData, email: e.target.value})} className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white" required />
                </div>
                <div>
                  <label className="text-xs text-gray-600 uppercase tracking-wider mb-2 block">Phone</label>
                  <input value={formData.phone} onChange={(e) => setFormData({...formData, phone: e.target.value})} className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white" required />
                </div>
                <div>
                  <label className="text-xs text-gray-600 uppercase tracking-wider mb-2 block">Net Worth Range</label>
                  <select value={formData.netWorth} onChange={(e) => setFormData({...formData, netWorth: e.target.value})} className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white" required>
                    <option value="">Select</option>
                    <option value="below-2cr">Below ₹2 Cr</option>
                    <option value="2-5cr">₹2-5 Cr</option>
                    <option value="5cr+">₹5+ Cr</option>
                    <option value="10cr+">₹10+ Cr</option>
                  </select>
                </div>
                <div>
                  <label className="text-xs text-gray-600 uppercase tracking-wider mb-2 block">Investment Intent</label>
                  <select value={formData.investmentIntent} onChange={(e) => setFormData({...formData, investmentIntent: e.target.value})} className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white" required>
                    <option value="">Select</option>
                    <option value="1cr">₹1 Cr</option>
                    <option value="5cr">₹5 Cr</option>
                    <option value="10cr+">₹10+ Cr</option>
                  </select>
                </div>
                <div className="col-span-2">
                  <label className="text-xs text-gray-600 uppercase tracking-wider mb-2 block">Message / Questions</label>
                  <textarea value={formData.message} onChange={(e) => setFormData({...formData, message: e.target.value})} rows={4} className="w-full bg-white/5 border border-white/10 px-4 py-3 text-white" />
                </div>
              </div>

              <button type="submit" className="w-full bg-[#4ADE80] hover:bg-[#4ADE80]/90 text-black font-semibold py-4 text-sm uppercase tracking-wider transition-all">
                Submit Enquiry
              </button>
            </form>
          ) : (
            <div className="bg-green-500/10 border border-green-500/30 p-8 text-center">
              <Check className="w-12 h-12 text-green-500 mx-auto mb-4" />
              <p className="text-white font-medium mb-2">Enquiry Received</p>
              <p className="text-sm text-gray-400">Our fund team will review your profile and contact you within 48 hours.</p>
            </div>
          )}
        </div>
      </div>

      {/* Regulatory Disclaimers */}
      <div className="border border-white/10 p-8 bg-white/5">
        <h3 className="text-sm text-white uppercase tracking-wider mb-4">Regulatory Disclosure</h3>
        <div className="space-y-3 text-xs text-gray-500 leading-relaxed">
          <p>This investment is suitable only for sophisticated investors as defined under SEBI (Alternative Investment Funds) Regulations, 2012.</p>
          <p>Investments in AIFs are subject to market risks. Past performance is not indicative of future results. Please read the offering document carefully before investing.</p>
          <p>This is not an offer to invest. Investments will be accepted only after due diligence and execution of subscription agreements.</p>
          <p>The fund is managed by AltSlice Asset Management Private Limited, registered with SEBI as a Category II AIF (Registration No: IN/AIF2/XX-XXXXX/XXXX).</p>
        </div>
      </div>
    </div>
  );
}
